import { axiosSecuredInstance } from "configurations/axiosConfig";
import {
  createHook,
  createStore,
  StoreActionApi,
} from "react-sweet-state";

type StoreApi = StoreActionApi<State>;
interface State {
  uniqueFileId: number;
  integrationKey: string;
  isFileUniqueLoading: boolean;
  isDuplicatedFile: boolean;
  prevFileNameValueState: number;
  showAlertDuplicatedFile: boolean;
  error: string;
  isToggleFileAgencyFocus: string;
  isOpeningExistingFile: boolean;
}

// Fail-safe for isOpeningExistingFile: the flag lives in a module-level store, so a
// stuck value would silently swallow a legitimate duplicated-file warning later on.
const OPENING_EXISTING_FILE_SUPPRESSION_MS = 10000;
let openingExistingFileTimer: ReturnType<typeof setTimeout> | undefined;

const Store = createStore<State, any>({
  initialState: {
    uniqueFileId: 0,
    integrationKey: "",
    isFileUniqueLoading: false,
    isDuplicatedFile: false,
    prevFileNameValueState: Date.now(),
    showAlertDuplicatedFile: false,
    error: "",
    isToggleFileAgencyFocus: "",
    isOpeningExistingFile: false,
  },
  actions: {
    getUniqueFileByAgencyClientFileId:
      (agencyId: string, clientFileId: string) =>
        async ({ setState }: StoreApi) => {
          try {
            setState({ isFileUniqueLoading: true, error: "" });

            const { data } = await axiosSecuredInstance.get(
              `/files/GetFileByAgencyClientFile/${agencyId}/${clientFileId}`
            );
            const fileId = data ? data.FileID : 0;
            const integrationKey = data ? data.IntegrationKey : "";

            setState({ isFileUniqueLoading: false, uniqueFileId: fileId, integrationKey: integrationKey });

            return fileId;
          } catch (error: any) {
            setState({ error });
          }
        },
    setIsDuplicatedFile:
      (value: boolean) =>
        ({ setState }: StoreApi) => {
          setState({ isDuplicatedFile: value });
        },
    setShowAlertDuplicatedFile:
      (openAlert: boolean) =>
        ({ setState, getState }: StoreApi) => {
          // The user explicitly picked this file from the autocomplete, so a
          // "file already exists" prompt is noise while we navigate to it.
          // Only opening the alert is suppressed; closing it must always work.
          if (openAlert && getState().isOpeningExistingFile) return;
          setState({ showAlertDuplicatedFile: openAlert });
        },
    setIsOpeningExistingFile:
      (value: boolean) =>
        ({ setState }: StoreApi) => {
          if (openingExistingFileTimer) {
            clearTimeout(openingExistingFileTimer);
            openingExistingFileTimer = undefined;
          }
          setState({ isOpeningExistingFile: value });
          if (value) {
            openingExistingFileTimer = setTimeout(() => {
              openingExistingFileTimer = undefined;
              setState({ isOpeningExistingFile: false });
            }, OPENING_EXISTING_FILE_SUPPRESSION_MS);
          }
        },
    setPrevFileNameValueState:
      (timestamp: number) =>
        ({ setState }: StoreApi) => {
          setState({ prevFileNameValueState: timestamp });
        },
        setToggleFileAgencyFocus:
        (value: string) =>
        ({ setState }: StoreApi) => {
          setState({ isToggleFileAgencyFocus: value });
        },
  },

  name: "file",
});

const hook = createHook(Store);
const useDuplicatedFileCheck = () => {
  return hook();
};
const useDuplicateFileCheckActions = createHook(Store, {
  selector: null,
});

export { useDuplicatedFileCheck, useDuplicateFileCheckActions };

